import { ApolloError } from '@apollo/client';

import type { ISurveyAnalyticsRow } from './analyticsModel';

/**
 * Spec 0098 — «Обзор» опросов: the vocabulary of the insights screen
 * (group timeline, AI digests, enriched comments). Pure functions only, so
 * the rules are unit-testable without Apollo.
 */

export interface ISurveyTimelineRow {
  assignmentId: string;
  surveyId: string;
  surveyTitle: string;
  lessonNumber?: number | null;
  lessonDate?: string | null;
  eligible: number;
  responded: number;
  avgScore?: number | null;
  negativeShare?: number | null;
  avgRating?: number | null;
  avgNps?: number | null;
}

/** `metric` '' = untagged questions. `avgScore` is on the 0..100 index. */
export interface ISurveyMetricRow {
  metric: string;
  avgScore?: number | null;
  answers: number;
}

export interface ISurveyGroupTimeline {
  rows: ISurveyTimelineRow[];
  metrics: ISurveyMetricRow[];
}

/** Response rate of one timeline row, `null` when nobody could answer. */
export const timelineResponseRate = (row: ISurveyTimelineRow): number | null =>
  row.eligible > 0
    ? Math.round((row.responded * 1000) / row.eligible) / 10
    : null;

/**
 * KPI strip: several buckets (courses, timeline rows) folded into one line.
 * Averages are weighted by what they average over — responses for the index,
 * NPS and negative share, the roster for the response rate — so a course
 * with 3 answers does not weigh as much as one with 300.
 */
export interface ISurveyKpi {
  responses: number;
  eligible: number;
  responseRate: number | null;
  avgScore: number | null;
  avgNps: number | null;
  negativeShare: number | null;
}

interface IKpiInput {
  responses: number;
  eligible?: number | null;
  avgScore?: number | null;
  avgNps?: number | null;
  negativeShare?: number | null;
}

const weighted = (
  items: ReadonlyArray<IKpiInput>,
  pick: (item: IKpiInput) => number | null | undefined,
  digits: number,
): number | null => {
  let sum = 0;
  let weight = 0;
  items.forEach((item) => {
    const value = pick(item);
    if (value === null || value === undefined || !item.responses) return;
    sum += value * item.responses;
    weight += item.responses;
  });
  if (!weight) return null;
  const factor = 10 ** digits;
  return Math.round((sum / weight) * factor) / factor;
};

export const foldSurveyKpi = (items: ReadonlyArray<IKpiInput>): ISurveyKpi => {
  const responses = items.reduce((acc, item) => acc + item.responses, 0);
  const eligible = items.reduce((acc, item) => acc + (item.eligible ?? 0), 0);
  return {
    responses,
    eligible,
    responseRate: eligible
      ? Math.round((Math.min(responses, eligible) * 1000) / eligible) / 10
      : null,
    avgScore: weighted(items, (item) => item.avgScore, 1),
    avgNps: weighted(items, (item) => item.avgNps, 1),
    negativeShare: weighted(items, (item) => item.negativeShare, 1),
  };
};

export const kpiFromAnalytics = (
  rows: ReadonlyArray<ISurveyAnalyticsRow>,
): ISurveyKpi =>
  foldSurveyKpi(
    rows.map((row) => ({
      responses: row.responsesCount,
      eligible: row.eligibleCount,
      avgScore: row.avgScore,
      avgNps: row.avgNps,
      negativeShare: row.negativeShare,
    })),
  );

export const kpiFromTimeline = (
  rows: ReadonlyArray<ISurveyTimelineRow>,
): ISurveyKpi =>
  foldSurveyKpi(
    rows.map((row) => ({
      responses: row.responded,
      eligible: row.eligible,
      avgScore: row.avgScore,
      avgNps: row.avgNps,
      negativeShare: row.negativeShare,
    })),
  );

/** Numeric order of `lesson_number` buckets («—» bucket last). */
export const byLessonNumber = (
  rows: ReadonlyArray<ISurveyAnalyticsRow>,
): ISurveyAnalyticsRow[] =>
  [...rows].sort((a, b) => {
    const left = Number(a.sortKey);
    const right = Number(b.sortKey);
    const leftOk = Number.isFinite(left);
    const rightOk = Number.isFinite(right);
    if (leftOk && rightOk) return left - right;
    if (leftOk) return -1;
    if (rightOk) return 1;
    return 0;
  });

/* ── AI digest ─────────────────────────────────────────────────────────── */

/**
 * ТЗ 0124: `course_lesson` — одно и то же занятие курса (по номеру в
 * расписании группы) у всех групп курса; `scopeId` — курс, номер занятия —
 * отдельным `lessonNumber`.
 */
export const SURVEY_DIGEST_SCOPES = [
  'course',
  'course_lesson',
  'group',
  'survey',
] as const;
export type SurveyDigestScope = typeof SURVEY_DIGEST_SCOPES[number];

export const SURVEY_DIGEST_STATUSES = [
  'queued',
  'running',
  'done',
  'failed',
] as const;
export type SurveyDigestStatus = typeof SURVEY_DIGEST_STATUSES[number];

export const isSurveyDigestStatus = (
  status: string,
): status is SurveyDigestStatus =>
  (SURVEY_DIGEST_STATUSES as readonly string[]).includes(status);

/** Still being produced — the list is polled while this holds. */
export const surveyDigestActive = (status?: string | null): boolean =>
  status === 'queued' || status === 'running';

/**
 * Перепроверка F26: сколько ждём незаконченную сводку, прежде чем считать её
 * зависшей. Бэкенд закрывает такие строки через 30 минут (`STALE_MINUTES`)
 * и сам — при чтении; минута сверху — на часы и сеть. Без этой границы
 * экран опрашивал зависшую строку каждые 4 с бесконечно, а «Пересобрать»
 * стояла мёртвой.
 */
export const SURVEY_DIGEST_STALE_MS = 31 * 60 * 1000;

/** Префикс `error` строки, которую бэкенд закрыл как зависшую. */
export const SURVEY_DIGEST_STALE_PREFIX = 'stale:';

/** Когда незаконченная сводка станет «зависшей» (мс эпохи) или `null`. */
export const surveyDigestStaleAt = (
  digest: Pick<ISurveyDigest, 'status' | 'createdAt'> | null | undefined,
): number | null => {
  if (!digest || !surveyDigestActive(digest.status)) return null;
  const created = Date.parse(digest.createdAt);
  return Number.isFinite(created) ? created + SURVEY_DIGEST_STALE_MS : null;
};

/**
 * Сводка зависла: бэкенд закрыл её с `stale:` либо она всё ещё «в работе»
 * дольше границы. Такая не блокирует «Пересобрать» и не опрашивается.
 */
export const surveyDigestStale = (
  digest:
    | Pick<ISurveyDigest, 'status' | 'createdAt' | 'error'>
    | null
    | undefined,
  now: number = Date.now(),
): boolean => {
  if (!digest) return false;
  if (digest.status === 'failed') {
    return (digest.error || '').trim().startsWith(SURVEY_DIGEST_STALE_PREFIX);
  }
  const staleAt = surveyDigestStaleAt(digest);
  return staleAt !== null && now >= staleAt;
};

/**
 * Через сколько можно повторить после отказа `digest_cooldown`: сервер пишет
 * «retry in N min». Нет числа — `fallbackMs`.
 */
export const surveyDigestRetryAfterMs = (
  message?: string | null,
  fallbackMs = 10 * 60 * 1000,
): number => {
  const match = /retry in (\d+)\s*min/i.exec(message || '');
  return match ? Number(match[1]) * 60 * 1000 : fallbackMs;
};

export interface ISurveyDigest {
  id: string;
  scope: string;
  scopeId: string;
  /** ТЗ 0124 — только у `course_lesson`. */
  lessonNumber?: number | null;
  status: string;
  textRu?: string | null;
  textPl?: string | null;
  model?: string | null;
  error?: string | null;
  createdAt: string;
  finishedAt?: string | null;
  byName?: string | null;
}

/**
 * Refusal codes of `generateSurveyDigest` (GraphQL `extensions.code`).
 * Each has its own sentence in `pages.surveys.insights.digest.refusal.*`.
 */
export const SURVEY_DIGEST_REFUSALS = [
  'digest_disabled',
  'bad_scope',
  'not_found',
  'digest_running',
  'digest_cooldown',
  'digest_daily_cap',
] as const;
export type SurveyDigestRefusal = typeof SURVEY_DIGEST_REFUSALS[number];

/**
 * Codes the backend writes at the START of `SurveyDigest.error`
 * («numbers_not_in_facts: 42, 17»). Known ones get a sentence; the raw
 * error stays visible underneath.
 */
export const SURVEY_DIGEST_FAILURES = [
  'digest_disabled',
  'numbers_not_in_facts',
  'bad_output',
  'model_error',
  'stale',
  'enqueue_failed',
] as const;
export type SurveyDigestFailure = typeof SURVEY_DIGEST_FAILURES[number];

/** `extensions.code` of the first coded GraphQL error, else `null`. */
export const surveyErrorCode = (
  error: ApolloError | null | undefined,
): string | null => {
  if (!error) return null;
  const coded = (error.graphQLErrors ?? []).find(
    (item) => typeof item?.extensions?.code === 'string',
  );
  return coded ? String(coded.extensions?.code) : null;
};

/**
 * A REAL permission refusal, as opposed to any other failure. The check moved
 * to `core/utils/isPermissionError` (one for the whole app — the group card
 * needs the same answer); this name stays as a re-export so callers keep
 * working.
 */
export { isPermissionError as isSurveyPermissionError } from 'core/utils/isPermissionError';

/** The failure code a stored `error` starts with, if it is a known one. */
export const surveyDigestFailureCode = (
  error?: string | null,
): SurveyDigestFailure | null => {
  if (!error) return null;
  const head = error.trim().split(/[:\s]/, 1)[0];
  return (SURVEY_DIGEST_FAILURES as readonly string[]).includes(head)
    ? (head as SurveyDigestFailure)
    : null;
};

/** Russian UI reads `textRu`, everything else `textPl` (the LMS/PL default). */
export const surveyUiIsRussian = (lang?: string | null): boolean =>
  (lang || '').toLowerCase().startsWith('ru');

/** Digest text in the UI language, falling back to the other one. */
export const surveyDigestText = (
  digest: Pick<ISurveyDigest, 'textRu' | 'textPl'> | null | undefined,
  lang: string,
): string => {
  if (!digest) return '';
  const primary = surveyUiIsRussian(lang) ? digest.textRu : digest.textPl;
  const secondary = surveyUiIsRussian(lang) ? digest.textPl : digest.textRu;
  return (primary || secondary || '').trim();
};

/**
 * Which digest to SHOW: the newest finished one. A queued/running/failed
 * newer attempt must not hide the text the team already has.
 */
export const pickSurveyDigest = (
  digests: ReadonlyArray<ISurveyDigest>,
): { latest: ISurveyDigest | null; shown: ISurveyDigest | null } => ({
  latest: digests[0] ?? null,
  shown: digests.find((item) => item.status === 'done') ?? null,
});

/* ── Enriched comments ─────────────────────────────────────────────────── */

export interface ISurveyAnswerInsight {
  lang?: string | null;
  textRu?: string | null;
  textPl?: string | null;
  sentiment?: string | null;
  isProblem: boolean;
  topic?: string | null;
  summary?: string | null;
}

/**
 * What a comment card shows: the translation into the UI language when one
 * exists and differs from the original, otherwise the original text.
 * `translated` tells the card whether the «Оригинал» toggle makes sense.
 */
export const surveyCommentDisplay = (
  text: string,
  insight: ISurveyAnswerInsight | null | undefined,
  lang: string,
): { text: string; translated: boolean } => {
  if (!insight) return { text, translated: false };
  const ru = surveyUiIsRussian(lang);
  const sameLanguage = ru ? insight.lang === 'ru' : insight.lang === 'pl';
  const translation = ru ? insight.textRu : insight.textPl;
  if (sameLanguage || !translation || translation.trim() === text.trim()) {
    return { text, translated: false };
  }
  return { text: translation, translated: true };
};

/* ── Group students (`surveyGroupStudents`) ────────────────────────────── */

export interface IGroupStudentSurveyRow {
  studentId: string;
  fullName: string;
  /** Attachments the student could answer (was on the roster). */
  eligible: number;
  responded: number;
  /** Mean of the responses' 0..100 index, `null` when nothing was scored. */
  avgScore: number | null;
  negativeCount: number;
  lastAnsweredAt: string | null;
  /** Newest free-text answer of the student, with its date. */
  lastComment: string | null;
  lastCommentAt: string | null;
  /** AI enrichment of that comment (translation, sentiment), if any. */
  lastCommentInsight: ISurveyAnswerInsight | null;
}

/** The API row as `ErpSurveyGroupStudents` returns it. */
export interface ISurveyGroupStudentApiRow {
  studentId: string;
  studentName: string;
  eligible: number;
  responded: number;
  avgScore?: number | null;
  negativeCount: number;
  lastAnsweredAt?: string | null;
  lastComment?: string | null;
  lastCommentAt?: string | null;
  lastCommentInsight?: ISurveyAnswerInsight | null;
}

/** API row → table row (`studentName` → `fullName`, absent → null). */
export const groupStudentRowFromApi = (
  row: ISurveyGroupStudentApiRow,
): IGroupStudentSurveyRow => ({
  studentId: row.studentId,
  fullName: row.studentName,
  eligible: row.eligible,
  responded: row.responded,
  avgScore: row.avgScore ?? null,
  negativeCount: row.negativeCount,
  lastAnsweredAt: row.lastAnsweredAt ?? null,
  lastComment: row.lastComment ?? null,
  lastCommentAt: row.lastCommentAt ?? null,
  lastCommentInsight: row.lastCommentInsight ?? null,
});

export type GroupStudentSortKey =
  | 'fullName'
  | 'responded'
  | 'avgScore'
  | 'negativeCount'
  | 'lastCommentAt';

/** Sort with nulls always last, whatever the direction. */
export const sortGroupStudents = (
  rows: ReadonlyArray<IGroupStudentSurveyRow>,
  key: GroupStudentSortKey,
  direction: 'asc' | 'desc',
): IGroupStudentSurveyRow[] => {
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = a[key];
    const right = b[key];
    const leftEmpty = left === null || left === undefined;
    const rightEmpty = right === null || right === undefined;
    if (leftEmpty && rightEmpty) return a.fullName.localeCompare(b.fullName);
    if (leftEmpty) return 1;
    if (rightEmpty) return -1;
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * sign || a.fullName.localeCompare(b.fullName);
    }
    return String(left).localeCompare(String(right)) * sign;
  });
};
